import logger from '#utils/logger';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

const getApiUrl = () => {
  const url = import.meta.env.VITE_API_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : `https://${url}`;
};

const API_URL = getApiUrl();

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = cookies.get('accessToken');
    if (
      token &&
      config.headers.get('X-Service-Method') !== 'completePasswordSetup' &&
      config.headers.get('X-Service-Method') !== 'resetPassword'
    ) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (axios.isCancel(error)) {
      const abortError = new Error(error.message);
      abortError.name = 'AbortError';
      return Promise.reject(abortError);
    }

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const { status, data } = error.response;

      if (
        error.config?.headers?.get('X-Service-Method') ===
        'completePasswordSetup'
      ) {
        return Promise.reject(
          new Error(
            status === 400 || status === 401
              ? 'This password link is invalid or expired'
              : 'Could not set your password. Please try again.'
          )
        );
      }

      // Handle common error cases
      if (status === 401) {
        // Remove token
        cookies.remove('accessToken', {
          path: '/',
          domain: import.meta.env.VITE_APP_DOMAIN,
        });

        // Prevent infinite redirect loops
        if (window.location.pathname !== '/auth') {
          window.location.href = '/auth';
        }

        // Return the error response directly without throwing
        return Promise.resolve({
          data: null,
          status: 401,
          statusText: 'Unauthorized',
          headers: error.response?.headers || {},
          config: error.config,
          isError: true,
          error: {
            message: 'Session expired. Please log in again.',
            ...(data as object),
          },
        } as any);
      }

      // Handle other error statuses
      if (status === 403) {
        // Forbidden - user doesn't have permission
        logger.error('Access denied');
      } else if (status === 404) {
        // Resource not found
        logger.error('Resource not found');
      } else if (status >= 500) {
        // Server error
        logger.error('Server error occurred');
      }

      // For all other errors, reject with the error
      if (status !== 401) {
        return Promise.reject(
          new Error(
            `API Error: ${JSON.stringify({
              ...error,
              data,
            })}`
          )
        );
      }
    }
    if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser
      logger.error('Network error occurred');
      return Promise.reject(new Error('Network error occurred'));
    }
    // Something happened in setting up the request that triggered an Error
    logger.error(error.message);
    return Promise.reject(new Error(error.message));
  }
);

// Helper function to handle API calls
export const api = {
  get: async <T>(
    url: string,
    config?: AxiosRequestConfig & { signal?: AbortSignal }
  ) => {
    const response = await apiClient.get<T>(url, config);
    return response.data;
  },
  post: async <T>(url: string, data: any, config?: AxiosRequestConfig) => {
    const response = await apiClient.post<T>(url, data, config);
    return response.data;
  },
  patch: async <T>(url: string, data: any, config?: AxiosRequestConfig) => {
    const response = await apiClient.patch<T>(url, data, config);
    return response.data;
  },
  delete: async <T>(url: string, config?: AxiosRequestConfig) => {
    const response = await apiClient.delete<T>(url, config);
    return response.data;
  },
};

export default apiClient;
